// 바인딩(Binding)을 일반 사용자가 한글로 읽을 수 있는 요약으로 변환한다.
// (b) 속성 패널 readout · (c) 캔버스 배지 hover 툴팁 공용 헬퍼.
//
// 표시 텍스트는 카탈로그 메타(group/entry.label/field.label, 모두 한글)에서 끌어오고,
// 키(catalogKey/bindingKey/path)는 개발자용 보조로 별도 노출한다.
// 결정 기록: docs/decisions/2026-06-22-composer-binding-visibility-korean-ux.md

import type { Binding, CatalogEntry, CatalogField, DataSourceDef } from '@safetyhub/shared';
import { flattenCatalogPaths, resolveSelectField } from './catalogPaths';

/** CatalogField.type 한글화. */
export function koreanFieldType(type: CatalogField['type']): string {
  switch (type) {
    case 'string':
      return '텍스트';
    case 'number':
      return '숫자';
    case 'date':
      return '날짜';
    case 'boolean':
      return '예/아니오';
    case 'array':
      return '목록';
    case 'object':
      return '객체';
    case 'enum':
      return '선택지';
    default:
      return type;
  }
}

/** lifecycle 한글화. */
export function koreanLifecycle(lifecycle: Binding['lifecycle']): string {
  return lifecycle === 'live' ? '조회 시 갱신' : '작성 시 고정';
}

/**
 * 바인딩 요약 표시 구조체.
 * - `parts`: 한글 요약 조각들(group ▸ label · field(타입) · lifecycle). UI에서 ' · '로 join.
 * - `text`: parts를 ' · '로 join한 한 줄 문자열(툴팁/배지용).
 * - `keys`: 개발자용 보조 키들(작게/모노/툴팁 노출).
 * - `legacy`: 카탈로그 entry를 못 찾고 레거시 source로만 표현되는 경우 true.
 */
export interface BindingSummary {
  parts: string[];
  text: string;
  keys: { catalogKey?: string; bindingKey?: string; select?: string };
  legacy: boolean;
}

/**
 * Binding + catalog → 한글 요약.
 * catalogKey로 entry를 찾으면 [group ▸ entry.label] · [field.label(타입)] · [lifecycle].
 * entry 없고 레거시 source만 있으면 legacy=true + 회색 표기용 텍스트.
 */
export function buildBindingSummary(
  binding: Binding,
  catalog: CatalogEntry[],
): BindingSummary {
  const keys = {
    catalogKey: binding.catalogKey,
    bindingKey: binding.bindingKey,
    select: binding.select,
  };

  const entry = binding.catalogKey
    ? catalog.find((e) => e.key === binding.catalogKey)
    : undefined;

  // 카탈로그 entry 기반(신).
  if (entry) {
    const parts: string[] = [`${entry.group} ▸ ${entry.label}`];

    if (binding.select) {
      const flat = flattenCatalogPaths(entry.returns);
      const field = flat.find((f) => f.path === binding.select);
      if (field) {
        parts.push(`${field.label} (${koreanFieldType(field.type as CatalogField['type'])})`);
      } else {
        // entry는 있으나 select 경로가 returns에 없음(드리프트) → 경로만 회색 보조로.
        parts.push(`경로: ${binding.select}`);
      }
    } else {
      parts.push('전체');
    }

    parts.push(koreanLifecycle(binding.lifecycle));

    return { parts, text: parts.join(' · '), keys, legacy: false };
  }

  // 레거시 source/api.
  if (binding.source !== undefined || binding.api !== undefined) {
    const legacyBits: string[] = [];
    if (binding.source !== undefined) legacyBits.push(`source=${binding.source}`);
    if (binding.api?.endpoint !== undefined) legacyBits.push(`api=${binding.api.endpoint}`);
    const parts = [`(레거시: ${legacyBits.join(', ')})`, koreanLifecycle(binding.lifecycle)];
    return { parts, text: parts.join(' · '), keys, legacy: true };
  }

  // 출처 미지정(bindingKey만 있는 빈 바인딩).
  const parts = ['출처 미지정', koreanLifecycle(binding.lifecycle)];
  return { parts, text: parts.join(' · '), keys, legacy: false };
}

/** 조건 키 출처 해석 결과 — 키 select 표시 라벨 + 그 값이 어디서 오는지 밝히는 문구. */
export interface ConditionSourceInfo {
  /** 사람 라벨 — 조건 키 select에 키 대신 표시한다. */
  label: string;
  /** 출처 문구(키를 뺀 꼬리) — 호출측이 `키 — ${sourceText}` 꼴 툴팁으로 조립한다. */
  sourceText: string;
}

/**
 * branch `conditionBindings` 한 행의 **출처를 사람 말로** 해석한다(못 풀면 undefined).
 *
 * `buildBindingSummary`를 쓸 수 없는 이유: 그쪽은 catalogKey가 없는 바인딩을 "출처 미지정"으로
 * 떨군다(dataSources를 모른다). 그런데 조건 데이터는 **두 갈래가 실제로 병존한다** — 같은 TBM
 * 템플릿 안에서 `tbmIsMultiSession`은 카탈로그(`tbm-overview`▸`isMultiSession`)를, `tbmHasPhotos`
 * 는 데이터 소스(`tbmPhotos-src`)를 읽는다. 한 갈래만 다루면 나머지 키가 조용히 원문으로 남아
 * 비대칭이 그대로 재발한다.
 *
 * 카탈로그 필드 해석에 `resolveSelectField`를 쓰는 것은 편의가 아니라 **일치 요구**다 — 값 후보를
 * 만드는 `valueSetForKey`가 같은 함수를 쓰므로, 다른 해석기를 쓰면 라벨이 가리키는 필드와 값
 * 목록이 나온 필드가 어긋날 수 있다.
 *
 * 해석 실패(드리프트로 entry/소스가 사라진 키 등)는 undefined다 — 호출측이 키 원문으로 폴백해야
 * 한다. 빈 문자열 라벨은 내지 않는다(조용히 빈칸이 된 select는 "이름 없는 키"로 오독된다).
 *
 * 읽는 필드는 `catalogKey`/`select`/`bindingKey` 셋뿐이지만 파라미터는 `Binding` 전체로 받는다 —
 * `bindingKey`·`lifecycle`이 Binding의 **필수** 필드라, Pick으로 좁히면 spec 픽스처가 실물이
 * 아닌 조각이 되고(같은 key로 snapshot/live 두 행이라는 실전 형상을 표현할 수 없다) 조각 위에서
 * 통과한 검사는 실물에서 통하는지 말해 주지 못한다.
 */
export function conditionBindingSourceInfo(
  binding: Binding | undefined,
  catalog: CatalogEntry[],
  dataSources: Pick<DataSourceDef, 'key' | 'label'>[] | undefined,
): ConditionSourceInfo | undefined {
  if (!binding) return undefined;

  // ① 카탈로그 출처 — select가 풀리면 그 필드 이름이 가장 구체적이다(안 풀리면 entry 이름).
  const entry = binding.catalogKey
    ? catalog.find((e) => e.key === binding.catalogKey)
    : undefined;
  if (entry) {
    const entryLabel = entry.label?.trim();
    const fieldLabel = resolveSelectField(entry, binding.select)?.label?.trim();
    if (fieldLabel && entryLabel) {
      return { label: fieldLabel, sourceText: `'${entryLabel}' 카탈로그의 '${fieldLabel}' 값` };
    }
    if (fieldLabel) return { label: fieldLabel, sourceText: `'${fieldLabel}' 카탈로그 값` };
    if (entryLabel) return { label: entryLabel, sourceText: `'${entryLabel}' 카탈로그 값` };
  }

  // ② 데이터 소스 출처 — bindingKey가 소스 키면 그 소스의 표시명.
  const sourceLabel = binding.bindingKey
    ? (dataSources ?? []).find((d) => d.key === binding.bindingKey)?.label?.trim()
    : undefined;
  if (sourceLabel) return { label: sourceLabel, sourceText: `'${sourceLabel}' 데이터 소스의 값` };

  return undefined;
}
